import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Upload, X, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface DirectSupabaseUploadProps {
  onImageUploaded: (url: string) => void;
  initialImage?: string;
  className?: string;
}

export const DirectSupabaseUpload: React.FC<DirectSupabaseUploadProps> = ({
  onImageUploaded,
  initialImage,
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | undefined>(initialImage);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadToSupabase = async (file: File): Promise<string> => {
    console.log('[DirectSupabaseUpload] Starting direct Supabase upload...');
    console.log('[DirectSupabaseUpload] File details:', {
      name: file.name,
      type: file.type,
      size: file.size
    });
    
    // No authentication required for interview subject photos
    
    // Create unique filename
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    
    // Determine correct MIME type based on file extension as fallback
    const getMimeType = (extension: string) => {
      const mimeTypes: { [key: string]: string } = {
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'webp': 'image/webp'
      };
      return mimeTypes[extension] || 'image/jpeg';
    };
    
    const contentType = file.type && file.type.startsWith('image/') 
      ? file.type 
      : getMimeType(fileExt || 'jpg');
    
    console.log('[DirectSupabaseUpload] Using content type:', contentType);
    console.log('[DirectSupabaseUpload] Uploading to photoalbum bucket:', fileName);

    // Upload to Supabase Storage (photoalbum is the correct bucket ID)
    const { data, error } = await supabase.storage
      .from('photoalbum')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: contentType
      });

    if (error) {
      console.error('[DirectSupabaseUpload] Upload error:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }

    console.log('[DirectSupabaseUpload] Upload data:', data);

    // Get public URL from photoalbum bucket
    const { data: publicUrlData } = supabase.storage
      .from('photoalbum')
      .getPublicUrl(fileName);

    const originalUrl = publicUrlData.publicUrl;
    // Ensure the URL has the correct "/object" segment
    const correctedUrl = originalUrl.includes('/object/') 
      ? originalUrl 
      : originalUrl.replace('/storage/v1/public/', '/storage/v1/object/public/');

    console.log('[DirectSupabaseUpload] Public URL (corrected):', correctedUrl);
    return correctedUrl;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPG, PNG, GIF or WEBP image.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setIsUploading(true);
    toast.loading('Laster opp bilde...', { id: 'upload-photo' });

    try {
      const url = await uploadToSupabase(file);
      setImageUrl(url);
      onImageUploaded(url);
      toast.success('Bilde lastet opp', { id: 'upload-photo' });
      console.log('[DirectSupabaseUpload] SUCCESS - Final URL:', url);
    } catch (error) {
      console.error('[DirectSupabaseUpload] Upload error:', error);
      toast.error(
        `Upload feilet: ${error instanceof Error ? error.message : 'Ukjent feil'}`,
        { id: 'upload-photo' }
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setImageUrl(undefined);
    onImageUploaded('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileChange}
        disabled={isUploading}
      />

      {imageUrl ? (
        <div className="relative inline-block">
          <img
            src={imageUrl}
            alt="Uploaded"
            className="max-h-32 rounded-md object-cover"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -top-2 -right-2 rounded-full h-6 w-6"
            onClick={handleRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-full"
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Laster opp...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Last opp profilbilde
            </>
          )}
        </Button>
      )}
    </div>
  );
};

export default DirectSupabaseUpload;